/**
 * /sections/:id/packet — the closing packet (M38).
 *
 * The four documents the college receives when a con-ed section closes,
 * built from what Attest witnessed and printed as one file:
 *   1. Instructor agreement (PSA)
 *   2. Official attendance record
 *   3. Student roster — the attestation each person signed, with evidence
 *   4. Transcription sheet
 *   5. Student records — one per person, in the auditors' wording (M40)
 *
 * A value nobody has entered prints as a blank line, never a guess. The
 * "Agreement values" panel (no-print) is where the office fills them in;
 * the college's constants are typed once and reused on every section.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { useAuth } from '../auth/useAuth.js';

const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' });
const STAMP = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' });
const day = (d) => (d ? DAY.format(new Date(`${String(d).slice(0, 10)}T00:00:00Z`)) : null);
const stamp = (iso) => (iso ? STAMP.format(new Date(iso)) : null);
const short = (d) => (d ? `${Number(String(d).slice(5, 7))}/${Number(String(d).slice(8, 10))}/${String(d).slice(2, 4)}` : null);
const money = (n) => (n == null ? null : `$${Number(n).toFixed(2)}`);
const h = (n) => `${Number(n ?? 0).toFixed(Number(n ?? 0) % 1 === 0 ? 0 : 2)}`;

/** A field on the paper form: label, then the value or a blank line. */
function F({ label, value, wide = false }) {
  const empty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
  return (
    <div className={`psa__f ${wide ? 'psa__f--wide' : ''}`}>
      <span className="psa__l">{label}</span>
      <span className={`psa__v ${empty ? 'psa__v--blank' : ''}`}>{empty ? ' ' : Array.isArray(value) ? value.join(', ') : String(value)}</span>
    </div>
  );
}

export function PacketPage() {
  const { id } = useParams();
  const [p, setP] = useState(undefined);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [busy, setBusy] = useState(false);
  const { profile } = useAuth();
  const canSend = profile?.permissions?.includes('class.manage') ?? false;

  const load = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/v1/classes/${id}/packet`);
      setP(d);
      setError(null);
    } catch (err) {
      setError(err);
      setP(null);
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  // The file the office receives: rendered on the server, same JSON as this page.
  const download = async (path, filename) => {
    setBusy(true);
    try {
      const { supabase } = await import('../lib/supabase.js');
      const { baseUrl } = await import('../lib/api.js');
      const { data } = await supabase.auth.getSession();
      const res = await fetch(`${baseUrl}/api/v1/classes/${id}/packet/${path}`, {
        headers: { Authorization: `Bearer ${data?.session?.access_token ?? ''}` },
      });
      if (!res.ok) throw new Error(`Could not build the file (${res.status}).`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const sectionSlug = () => (p?.agreement.section.section_id ?? 'section').replace(/[^A-Za-z0-9-]+/g, '_');
  const downloadPdf = () => download('pdf', `Closing_packet_${sectionSlug()}${p?.ready ? '' : '_DRAFT'}.pdf`);
  // The college's own Attendance Record workbook, filled — plug and play.
  const downloadXlsx = () => download('attendance.xlsx', `${sectionSlug().replace(/-/g, ' ')} Attendance${p?.ready ? '' : ' DRAFT'}.xlsx`);

  if (p === undefined) return <p className="muted">Building the packet…</p>;
  if (!p) {
    return (
      <>
        <p className="crumb">
          <Link to={`/sections/${id}`}>← Back to section</Link>
        </p>
        <ErrorBanner error={error} />
      </>
    );
  }

  const a = p.agreement;
  const att = p.attendance;
  const hd = att.header;
  const ready = p.ready;

  return (
    <>
      <p className="crumb no-print">
        <Link to={`/sections/${id}`}>← Back to section</Link> · <Link to={`/sections/${id}/record`}>Attendance record only</Link>
      </p>
      <ErrorBanner error={error} />

      <div className="toolbar no-print">
        <button type="button" className="btn btn--primary btn--auto" disabled={busy} onClick={downloadPdf}>
          {busy ? 'Building…' : 'Download the packet (PDF)'}
        </button>
        <button type="button" className="btn btn--auto" disabled={busy} onClick={downloadXlsx}>
          Attendance Record (.xlsx)
        </button>
        <button type="button" className="btn btn--auto" onClick={() => window.print()}>
          Print this page
        </button>
        <button type="button" className="btn btn--auto" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Close agreement values' : 'Agreement values'}
        </button>
        {canSend && (
          <button type="button" className="btn btn--auto" onClick={() => setEmailing((v) => !v)}>
            {emailing ? 'Close email' : 'Email the packet'}
          </button>
        )}
        <span className="muted">
          {ready ? '✓ Every session certified — this packet is final.' : `Draft — ${att.certification.certified_sessions} of ${att.certification.total_sessions} sessions certified.`}
        </span>
      </div>

      {editing && <ContractEditor sectionId={id} packet={p} busy={busy} setBusy={setBusy} setError={setError} reload={load} />}
      {emailing && <EmailPacket sectionId={id} packet={p} />}

      {/* ---------------------------------------------------------------- 1 */}
      <article className={`record psa ${ready ? '' : 'record--draft'}`}>
        {!ready && (
          <div className="record__watermark" aria-hidden="true">
            DRAFT
          </div>
        )}
        <header className="psa__head">
          <div>
            <strong>{a.college.name ?? 'Asheville-Buncombe Technical Community College'}</strong>
            <br />
            {a.college.address_lines?.length ? a.college.address_lines.join(' · ') : '340 Victoria Rd · Asheville, NC 28801'}
            <br />
            {a.college.division}
          </div>
          <div className="psa__title">
            Instructor Agreement
            <br />
            <span className="muted">{a.section.section_id}</span>
          </div>
        </header>

        <div className="psa__grid">
          <F label="Section" value={a.section.section_id} />
          <F label="Subject code" value={a.section.subject_code} />
          <F label="CCL course #" value={a.section.ccl_course_code} />
          <F label="COCO course #" value={a.section.coco_course_number} />
          <F label="Term" value={a.section.term} />
          <F label="Course title" value={a.section.course_title} wide />
          <F label="Official CCL title" value={a.section.official_title} wide />
          <F label="Begin date" value={short(a.section.begin_date)} />
          <F label="End date" value={short(a.section.end_date)} />
          <F label="10% date" value={short(a.section.census_date)} />
          <F label="Sched. type" value={a.section.schedule_type === 'non_regular' ? 'Non-regular' : a.section.schedule_type === 'hyflex' ? 'HyFlex' : a.section.schedule_type ? 'Regular' : null} />
          <F label="Total class hrs" value={a.section.total_class_hours} />
          <F label="CEUs" value={a.section.ceus} />
          <F label="Dates" value={a.section.dates.map(short)} wide />
          <F label="Days" value={a.section.days_met} />
          <F label="Weeks class meets" value={a.section.weeks_met || null} />
          <F label="Start time" value={a.section.start_time} />
          <F label="End time" value={a.section.end_time} />
          <F label="Site / location" value={a.section.site ?? a.section.location} />
          <F label="Building" value={a.section.building} />
          <F label="Room #" value={a.section.room} />
          <F label="Online reg. / visibility" value={a.section.online_visibility} />
          <F label="Min # students" value={a.section.min_students} />
          <F label="Max # students" value={a.section.max_students} />
          <F label="# allowed on wait list" value={a.section.wait_list} />
        </div>

        <h3 className="psa__h">Fees</h3>
        <div className="psa__grid">
          <F label="AR code" value={a.fees.ar_code} />
          <F label="Reg. fee" value={money(a.fees.registration_fee)} />
          <F label="Tech fee" value={a.fees.tech_fee_code} />
          <F label="Ins. fee" value={a.fees.insurance_fee_code} />
          <F label="Supply fee" value={a.fees.supply_fee_code} />
          <F label="CAPS fee" value={a.fees.caps_fee_code} />
          <F label="Discounts" value={a.fees.discounts ?? a.fees.waiver_codes} wide />
        </div>

        <h3 className="psa__h">Instructor and pay</h3>
        <div className="psa__grid">
          <F label="Instructor name" value={a.instructor.name} wide />
          <F label="Instructor ID (Colleague)" value={a.instructor.colleague_id} />
          <F label="Lead instructor?" value={a.instructor.name ? 'Yes' : null} />
          <F label="Address" value={a.instructor.address} wide />
          <F label="Pay on PSA" value={a.pay.basis === 'no_pay' ? 'No pay' : a.pay.basis === 'hourly' ? 'Hourly' : null} />
          <F label="Hourly rate" value={a.pay.basis === 'hourly' ? money(a.pay.hourly_rate) : a.pay.basis === 'no_pay' ? 'no pay' : null} />
          <F label="Total hours" value={a.pay.pay_hours} />
          <F label="Pay for month of" value={a.pay.pay_month} />
          <F label="Total course cost" value={money(a.pay.total_course_cost)} />
          <F label="Budget number" value={a.pay.budget_number} wide />
          <F label="Supervisor's name" value={a.supervisor.name} />
          <F label="Supervisor's Colleague ID" value={a.supervisor.colleague_id} />
          <F label="Special instructions / comments" value={a.comments ?? a.instructor.employee_status} wide />
        </div>

        <p className="psa__fine">
          This agreement is a commitment for instructional duties for the class(es) listed above. Professional responsibilities
          inherent in the instructor&apos;s role include preparation and presentation, record keeping and reporting of attendance and
          grades, student consultations, attending up to three required Professional Development trainings per semester, and other
          class-related activities which may be necessary outside of scheduled class hours. The commitment for payment is based on
          sufficient enrollment and availability of funds. Student attrition may cause this class to be terminated. Arrangements
          for substitutes must follow College procedure. Part-time instructors are not assured continuing employment. This
          agreement is subject to exception by the College President without prior or written notice.
        </p>

        <div className="psa__sign">
          <div>
            <div className="psa__sigline">
              {a.signatures.certified && a.signatures.instructor[0] ? (
                <span>
                  Electronically signed — {a.signatures.instructor_names[0] ?? 'Instructor'} · {stamp(a.signatures.instructor[0].certified_at)} ·
                  certification statement {a.signatures.instructor[0].statement_version}
                </span>
              ) : (
                ' '
              )}
            </div>
            <span className="psa__l">Instructor signature · date</span>
          </div>
          <div>
            <div className="psa__sigline">&nbsp;</div>
            <span className="psa__l">Director / Coordinator signature · date</span>
          </div>
        </div>
        <p className="psa__fine muted">
          The instructor&apos;s signature is the certification of accuracy recorded in Attest at close ({a.signatures.instructor.length} of{' '}
          {att.certification.total_sessions} sessions). {a.form_version}.
        </p>
      </article>

      {/* ---------------------------------------------------------------- 2 */}
      <article className={`record ${ready ? '' : 'record--draft'} page-break`}>
        {!ready && (
          <div className="record__watermark" aria-hidden="true">
            DRAFT
          </div>
        )}
        <header className="record__head">
          <h1>
            Official Attendance Record
            {!ready && <span className="record__flag"> — DRAFT, NOT CERTIFIED</span>}
          </h1>
          <p className="muted">
            {a.college.name ?? 'Asheville-Buncombe Technical Community College'} · {a.college.division}
          </p>
        </header>
        <dl className="record__grid">
          <dt>Section</dt>
          <dd>{a.section.section_id}</dd>
          <dt>Course title</dt>
          <dd>{a.section.course_title}</dd>
          <dt>Term</dt>
          <dd>{a.section.term ?? '—'}</dd>
          <dt>Start / end</dt>
          <dd>
            {day(hd.start_date)} – {day(hd.end_date)}
          </dd>
          <dt>Hours</dt>
          <dd>
            {hd.state_approved_hours ?? hd.scheduled_hours} total · {hd.scheduled_hours} scheduled
            {hd.hours_reconcile === false && <span className="record__flag"> · DOES NOT RECONCILE</span>}
          </dd>
          <dt>Lead instructor</dt>
          <dd>{att.instructors.join(', ') || '—'}</dd>
        </dl>
        <h2>Schedule</h2>
        <div className="schedule__scroll">
          <table className="schedule__table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Start</th>
                <th scope="col">End</th>
                <th scope="col">Location</th>
              </tr>
            </thead>
            <tbody>
              {att.meeting_schedule.map((m, i) => (
                <tr key={`${m.date}-${i}`}>
                  <td>{m.date}</td>
                  <td>{m.start}</td>
                  <td>{m.end}</td>
                  <td>{m.location || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <h2>
          Participants <span className="muted">({att.totals.enrolled})</span>
        </h2>
        <div className="schedule__scroll">
          <table className="schedule__table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Student ID</th>
                <th scope="col">Name</th>
                <th scope="col">Agency of record</th>
                <th scope="col">Position</th>
                <th scope="col">P/V</th>
                <th scope="col">Fee</th>
                <th scope="col">Contact hrs</th>
              </tr>
            </thead>
            <tbody>
              {att.participants.map((x, i) => (
                <tr key={x.enrollment_id}>
                  <td className="muted">{i + 1}</td>
                  <td>{x.identity_status === 'unconfirmed' ? <span className="record__flag">ID unconfirmed</span> : <code>{x.external_id ?? '—'}</code>}</td>
                  <td>
                    {x.full_name ?? '—'}
                    {x.withdrawal_date && <span className="muted"> · withdrew {x.withdrawal_date}</span>}
                  </td>
                  <td>{x.agency_name}</td>
                  <td>{x.role_title ?? '—'}</td>
                  <td>{x.worker_type === 'volunteer' ? 'V' : x.worker_type === 'paid' ? 'P' : '—'}</td>
                  <td>{x.fee_status === 'waived' ? (x.waiver_code ?? 'Waived') : x.fee_status === 'pending' ? 'Pending' : 'Invoiced'}</td>
                  <td>{x.attended ? h(x.contact_hours) : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="record__totals">
          <strong>{att.totals.attended}</strong> of {att.totals.enrolled} attended · <strong>{h(att.totals.contact_hours)}</strong> total
          contact hours<span className="muted"> · 60 minutes = 1 hour</span>
        </p>
        <section className="record__cert">
          <h2>Certification of accuracy</h2>
          <p>I certify that the attendance recorded above is a true and accurate record of the instruction delivered and of the persons who attended.</p>
          {ready ? (
            <ul className="plain-list">
              {att.certification.signatures.map((s) => (
                <li key={s.session_id}>
                  Signed {stamp(s.certified_at)}
                  <span className="muted"> · statement {s.cert_statement_version}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="record__unsigned">Not yet certified.</p>
          )}
        </section>
      </article>

      {/* ---------------------------------------------------------------- 3 */}
      <article className={`record ${ready ? '' : 'record--draft'} page-break`}>
        <header className="record__head">
          <h1>Student Roster</h1>
          <p className="muted">
            {a.college.name ?? 'A-B Tech'} {a.college.division} · {a.section.course_title} · {a.section.section_id} · {day(hd.start_date)}
          </p>
        </header>
        <p className="psa__fine">
          By signing below, you acknowledge and confirm the following: <em>&ldquo;{p.roster.attestation_text}&rdquo;</em> {p.roster.federal_note}
        </p>
        <p className="psa__fine muted">{p.roster.ferpa_note}</p>
        <div className="schedule__scroll">
          <table className="schedule__table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Student ID</th>
                <th scope="col">Printed name</th>
                <th scope="col">Agency</th>
                <th scope="col">Position title</th>
                <th scope="col">P/V</th>
                <th scope="col">Waiver</th>
                <th scope="col">Signature</th>
              </tr>
            </thead>
            <tbody>
              {p.roster.rows.map((r) => (
                <tr key={r.n}>
                  <td className="muted">{r.n}</td>
                  <td>{r.identity_status === 'unconfirmed' ? <span className="record__flag">ID unconfirmed</span> : <code>{r.external_id ?? '—'}</code>}</td>
                  <td>{r.printed_name}</td>
                  <td>{r.agency}</td>
                  <td>{r.position ?? '—'}</td>
                  <td>{r.worker_type === 'volunteer' ? 'V' : r.worker_type === 'paid' ? 'P' : '—'}</td>
                  <td>{r.waiver_code ?? '—'}</td>
                  <td>
                    {r.signed ? (
                      <span>
                        e-signed {stamp(r.signed_at)}
                        <span className="muted"> · {r.signature_method?.includes('electronic') ? 'attestation, electronic' : (r.signature_method ?? '')}</span>
                      </span>
                    ) : (
                      <span className="record__flag">not signed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="record__totals">
          <strong>{p.roster.signed}</strong> signed · {p.roster.unsigned} not signed
          {p.roster.unsigned > 0 && <span className="muted"> — an unsigned row is on the roster and earned credit; only the fee waiver is unsettled.</span>}
        </p>
        <div className="psa__sign">
          <div>
            <div className="psa__sigline">&nbsp;</div>
            <span className="psa__l">Coordinator / Director signature · date</span>
          </div>
        </div>
        <p className="psa__fine muted">{p.roster.form_version}. Each signature above was captured electronically at check-in against this statement, on the person&apos;s own device.</p>
      </article>

      {/* ---------------------------------------------------------------- 4 */}
      <article className={`record ${ready ? '' : 'record--draft'} page-break`}>
        <header className="record__head">
          <h1>Transcription Sheet</h1>
          <p className="muted">
            {a.section.section_id} · {a.section.course_title} · {a.section.term ?? ''}
          </p>
        </header>
        <div className="schedule__scroll">
          <table className="schedule__table">
            <thead>
              <tr>
                <th scope="col">Last name</th>
                <th scope="col">First name</th>
                <th scope="col">Student ID</th>
                <th scope="col">Attended</th>
                <th scope="col">Affiliation</th>
              </tr>
            </thead>
            <tbody>
              {p.transcription.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.last}</td>
                  <td>{r.first}</td>
                  <td>{r.identity_status === 'unconfirmed' ? <span className="record__flag">Reg requested</span> : <code>{r.external_id ?? '—'}</code>}</td>
                  <td>{r.tag}</td>
                  <td>{r.affiliation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="psa__fine muted">{p.transcription.note}</p>
      </article>

      {/* ---------------------------------------------------------------- 5
          One page per person: the attestation in the auditors' wording, then
          the account of the class. Brian, 2026-09-22 — "that is how they need
          the information to be presented on the course auditing side." */}
      {(p.student_records?.rows ?? []).map((sr) => (
        <article key={sr.roster_id} className={`record ${ready ? '' : 'record--draft'} page-break`}>
          <header className="record__head">
            <h1>{sr.name ?? 'Unnamed participant'}</h1>
            <p className="muted">
              {sr.identity_status === 'unconfirmed' ? 'A-B Tech ID: registration requested' : `A-B Tech ID ${sr.external_id ?? '—'}`} ·{' '}
              {a.section.section_id} · {a.section.course_title}
            </p>
          </header>

          <h2>Attestation</h2>
          <p className="srec__statement">{sr.attestation.statement}</p>
          <p className={sr.attestation.signed ? 'psa__fine muted' : 'record__flag'}>
            {sr.attestation.signed
              ? `Signed electronically ${stamp(sr.attestation.signed_at)} on the participant's own device.${sr.attestation.waiver_code ? ` Fee waiver ${sr.attestation.waiver_code}.` : ''}`
              : 'NOT SIGNED — on the roster and earned credit; only the fee waiver is unsettled.'}
          </p>

          <h2>The account of the class</h2>
          <div className="schedule__scroll">
            <table className="schedule__table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Location</th>
                  <th scope="col">Checked in</th>
                  <th scope="col">Checked out</th>
                  <th scope="col">Minutes</th>
                </tr>
              </thead>
              <tbody>
                {sr.attendance.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">No check-in recorded.</td>
                  </tr>
                )}
                {sr.attendance.map((at) => (
                  <tr key={at.session_id}>
                    <td>{at.date ? stamp(at.date).slice(0, 10) : ''}</td>
                    <td>{at.location ?? ''}</td>
                    <td>{at.time_in ? stamp(at.time_in) : ''}</td>
                    <td>{at.time_out ? stamp(at.time_out) : ''}</td>
                    <td>{at.contact_minutes ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="psa__fine muted">
            {sr.totals.contact_hours ?? 0} contact hours in total (60 minutes = 1 hour), computed by the database from time-in and
            time-out.
          </p>

          <h2>Skills</h2>
          {sr.skills.length ? (
            <table className="schedule__table">
              <thead>
                <tr>
                  <th>Skill sheet</th>
                  <th>Version</th>
                  <th>When</th>
                  <th>Result</th>
                  <th>Steps S</th>
                </tr>
              </thead>
              <tbody>
                {sr.skills.map((k) => (
                  <tr key={k.id}>
                    <td>
                      {k.skill}
                      {k.device ? ` (${k.device})` : ''}
                      {k.attempt > 1 ? ` · attempt ${k.attempt}` : ''}
                    </td>
                    <td>{k.version}</td>
                    <td>{stamp(k.evaluated_at)}</td>
                    <td>{{ PASS: 'Pass', REMEDIATE: 'Remediate', FAIL_CRITICAL: 'Fail (critical step)' }[k.result] ?? k.result}</td>
                    <td>
                      {k.steps_s} of {k.steps_total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="psa__fine muted">
              {sr.answers_available === false ? p.student_records.skills_unavailable_note ?? p.student_records.skills_note : p.student_records.skills_note}
            </p>
          )}

          <h2>Assessment</h2>
          {(sr.answers_available ?? p.student_records.answers_available) === false ? (
            <>
              <p className="psa__fine">
                {sr.assessment.items_asked} items were put to the room, {sr.assessment.completion_check_items} of them the final
                assessment.
              </p>
              <p className="record__flag">{p.student_records.answers_note}</p>
            </>
          ) : (
            <p className="psa__fine">
              {sr.assessment.items_answered} of {sr.assessment.items_asked} items answered · {sr.assessment.graded_correct} of{' '}
              {sr.assessment.graded_items} correct where the item has a right answer · final assessment{' '}
              {sr.assessment.completion_check_answered} of {sr.assessment.completion_check_items} answered.
            </p>
          )}
          <div className="schedule__scroll">
            <table className="schedule__table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Question</th>
                  <th scope="col">Their answer</th>
                  <th scope="col">Correct answer</th>
                  <th scope="col" aria-label="right or wrong" />
                </tr>
              </thead>
              <tbody>
                {sr.answers.map((an) => (
                  <tr key={an.position}>
                    <td>{an.position}</td>
                    <td>
                      {an.stem}
                      {an.is_completion_check && <span className="badge badge--published"> final</span>}
                    </td>
                    <td>
                      {an.answered === null ? (
                        <span className="muted">not available to this account</span>
                      ) : an.answered ? (
                        an.answer
                      ) : (
                        <span className="muted">did not answer</span>
                      )}
                    </td>
                    <td>{an.correct_answer ?? <span className="muted">—</span>}</td>
                    <td>{an.correct === null ? '' : an.correct ? '✓' : '✗'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2>Outcome</h2>
          <p className="psa__fine">
            {sr.outcome.result
              ? `${sr.outcome.result === 'complete' ? 'Complete' : 'Incomplete'} · ${sr.outcome.ce_hours ?? 0} CE hours awarded${
                  sr.outcome.withdrawn ? ` · withdrew ${String(sr.outcome.withdrawn).slice(0, 10)}` : ''
                }.`
              : 'No completion has been awarded yet — this record is a draft.'}
          </p>
        </article>
      ))}

      <p className="muted psa__fine">
        Packet generated by Attest {stamp(p.generated_at)} from the published snapshot (version {p.version}). Nothing above was typed for this
        packet except the agreement values marked as such.
      </p>
    </>
  );
}

/* ------------------------------------------------------------------------ */
const CONTRACT_FIELDS = [
  ['term', 'Term (e.g. 2026CE3)'],
  ['coco_course_number', 'COCO course #'],
  ['room', 'Room #'],
  ['building', 'Building'],
  ['site', 'Site / location'],
  ['online_visibility', 'Online reg. / visibility'],
  ['min_students', 'Min # students', 'number'],
  ['max_students', 'Max # students', 'number'],
  ['wait_list', '# allowed on wait list', 'number'],
  ['registration_fee', 'Registration fee ($)', 'number'],
  ['discounts', 'Discounts (waiver codes)'],
  ['instructor_name', 'Instructor name (if not the certifying instructor)'],
  ['instructor_colleague_id', 'Instructor Colleague ID'],
  ['instructor_address', 'Instructor address'],
  ['pay_basis', 'Pay on PSA', 'select', ['', 'no_pay', 'hourly']],
  ['hourly_rate', 'Hourly rate ($)', 'number'],
  ['pay_hours', 'Pay hours', 'number'],
  ['pay_month', 'Pay for month of'],
  ['total_course_cost', 'Total course cost ($)', 'number'],
  ['budget_number', 'Budget number'],
  ['supervisor_name', "Supervisor's name"],
  ['supervisor_colleague_id', "Supervisor's Colleague ID"],
  ['employee_status', 'Employee status (e.g. full-time employee — no pay contract)'],
  ['comments', 'Special instructions / comments'],
];
const DEFAULT_FIELDS = [
  ['college_name', 'College name'],
  ['division', 'Division'],
  ['site', 'Default site'],
  ['ar_code', 'AR code (e.g. CEDOE)'],
  ['registration_fee', 'Default registration fee ($)', 'number'],
  ['tech_fee_code', 'Tech fee code'],
  ['insurance_fee_code', 'Insurance fee code'],
  ['supply_fee_code', 'Supply fee code'],
  ['caps_fee_code', 'CAPS fee code'],
  ['budget_number', 'Default budget number'],
  ['supervisor_name', "Default supervisor's name"],
  ['supervisor_colleague_id', "Default supervisor's Colleague ID"],
];

function ContractEditor({ sectionId, packet, busy, setBusy, setError, reload }) {
  const [c, setC] = useState(() => ({ ...packet.contract_values }));
  const [d, setD] = useState(() => ({ ...packet.contract_defaults, address_lines: (packet.contract_defaults.address_lines ?? []).join(' | ') }));
  const [tab, setTab] = useState('section');

  const save = async () => {
    setBusy(true);
    try {
      if (tab === 'section') {
        const body = {};
        for (const [k, , type] of CONTRACT_FIELDS) {
          const v = c[k];
          body[k] = v === '' || v == null ? null : type === 'number' ? Number(v) : v;
        }
        await apiFetch(`/api/v1/classes/${sectionId}/packet/contract`, { method: 'PATCH', body: JSON.stringify(body) });
      } else {
        const body = {};
        for (const [k, , type] of DEFAULT_FIELDS) {
          const v = d[k];
          body[k] = v === '' || v == null ? null : type === 'number' ? Number(v) : v;
        }
        body.address_lines = String(d.address_lines ?? '')
          .split('|')
          .map((s) => s.trim())
          .filter(Boolean);
        await apiFetch(`/api/v1/classes/${sectionId}/packet/contract-defaults`, { method: 'PATCH', body: JSON.stringify(body) });
      }
      await reload();
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const field = (state, set, [k, label, type, options]) => (
    <label className="field" key={k}>
      <span>{label}</span>
      {type === 'select' ? (
        <select value={state[k] ?? ''} onChange={(e) => set({ ...state, [k]: e.target.value })}>
          {options.map((o) => (
            <option key={o} value={o}>
              {o === '' ? '—' : o === 'no_pay' ? 'No pay' : 'Hourly'}
            </option>
          ))}
        </select>
      ) : (
        <input type={type === 'number' ? 'number' : 'text'} step="any" value={state[k] ?? ''} onChange={(e) => set({ ...state, [k]: e.target.value })} />
      )}
    </label>
  );

  return (
    <section className="panel no-print">
      <h2>Agreement values</h2>
      <p className="muted">
        The blanks on the instructor agreement that Attest cannot witness. <strong>This section</strong> is what changes each time;{' '}
        <strong>College constants</strong> are typed once and printed on every section&apos;s agreement. Leave anything you don&apos;t
        know blank — it prints as a blank line for the office.
      </p>
      <div className="toolbar">
        <button type="button" className={`btn btn--auto ${tab === 'section' ? 'btn--primary' : ''}`} onClick={() => setTab('section')}>
          This section
        </button>
        <button type="button" className={`btn btn--auto ${tab === 'defaults' ? 'btn--primary' : ''}`} onClick={() => setTab('defaults')}>
          College constants
        </button>
      </div>
      <div className="psa__editgrid">
        {tab === 'section' ? CONTRACT_FIELDS.map((f) => field(c, setC, f)) : DEFAULT_FIELDS.map((f) => field(d, setD, f))}
        {tab === 'defaults' && (
          <label className="field">
            <span>Address lines (separate with |)</span>
            <input type="text" value={d.address_lines ?? ''} onChange={(e) => setD({ ...d, address_lines: e.target.value })} />
          </label>
        )}
      </div>
      <div className="toolbar">
        <button type="button" className="btn btn--primary btn--auto" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}

/**
 * Email the packet (M42). Final packets only; one message per address, each
 * logged with the PDF's fingerprint and what the mail service said. The
 * college's usual recipients are a College constant, so this is usually one
 * press. Sent to the office, it is a disclosure of student records — the
 * panel says so, and the log is the record of it.
 */
function EmailPacket({ sectionId, packet }) {
  const usual = packet.contract_defaults?.packet_recipients ?? [];
  const [to, setTo] = useState(usual.join(', '));
  const [xlsx, setXlsx] = useState(true);
  const [note, setNote] = useState('');
  const [remember, setRemember] = useState(usual.length === 0);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [sends, setSends] = useState(undefined);

  const loadSends = useCallback(async () => {
    try {
      setSends(await apiFetch(`/api/v1/classes/${sectionId}/packet/emails`));
    } catch (e) {
      setErr(e);
      setSends(null);
    }
  }, [sectionId]);
  useEffect(() => {
    loadSends();
  }, [loadSends]);

  const addresses = to
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

  const send = async () => {
    setSending(true);
    setErr(null);
    try {
      await apiFetch(`/api/v1/classes/${sectionId}/packet/email`, {
        method: 'POST',
        body: JSON.stringify({ to: addresses, include_xlsx: xlsx, note: note.trim() || null }),
      });
      if (remember) {
        // Best effort: the send already happened; failing to remember the
        // addresses must not read as a failed send.
        apiFetch(`/api/v1/classes/${sectionId}/packet/contract-defaults`, {
          method: 'PATCH',
          body: JSON.stringify({ packet_recipients: addresses }),
        }).catch(() => {});
      }
      setNote('');
      await loadSends();
    } catch (e) {
      setErr(e);
    } finally {
      setSending(false);
    }
  };

  const statusWord = { sent: 'sent', mail_disabled: 'mail is switched off', rejected: 'refused by the mail service', timeout: 'timed out', network: 'could not reach the mail service' };

  return (
    <section className="panel no-print" aria-labelledby="email-packet-heading">
      <h2 id="email-packet-heading">Email the packet</h2>
      {!packet.ready ? (
        <p className="callout callout--warn">
          <strong>Only a final packet is emailed.</strong>
          <span className="callout__detail">Certify every session that ran first, so the office never files a copy that later changes.</span>
        </p>
      ) : (
        <>
          <p className="muted">
            The PDF{xlsx ? ' and the attendance workbook' : ''} go as attachments, one message per address. This packet holds student records
            {(packet.student_records?.rows ?? []).some((r) => r.answers_available) ? ', including answers and skill sheets' : ''} — send it only to people at the college who need it.
            Every send is logged below.
          </p>
          <ErrorBanner error={err} />
          <label className="field">
            <span>To (up to ten addresses, separated by commas)</span>
            <input type="text" value={to} onChange={(e) => setTo(e.target.value)} placeholder="ce.office@abtech.edu" disabled={sending} />
          </label>
          <label className="field">
            <span>Note (optional)</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} disabled={sending} />
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={xlsx} onChange={(e) => setXlsx(e.target.checked)} disabled={sending} />
            <span>Attach the attendance record workbook (.xlsx) too</span>
          </label>
          <label className="checkbox">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={sending} />
            <span>Use these addresses by default for this college</span>
          </label>
          <div className="toolbar">
            <button type="button" className="btn btn--primary btn--auto" disabled={sending || addresses.length === 0 || addresses.length > 10} onClick={send}>
              {sending ? 'Sending…' : `Send to ${addresses.length || 'nobody yet'}${addresses.length === 1 ? ' address' : addresses.length > 1 ? ' addresses' : ''}`}
            </button>
          </div>
        </>
      )}

      <h3>Sent so far</h3>
      {sends === undefined && <p className="muted">Loading…</p>}
      {sends && sends.length === 0 && <p className="muted">This packet has not been emailed.</p>}
      {sends && sends.length > 0 && (
        <ul className="home__list">
          {sends.map((x) => (
            <li key={x.id} className="home__item">
              <div>
                <strong>{stamp(x.sent_at)}</strong> by {x.users?.full_name ?? 'someone'}
                {x.included_xlsx ? ' · PDF + workbook' : ' · PDF'}
                {x.answers_included ? ' · with answers' : ''}
                <div className="muted">
                  {(x.results ?? []).map((r) => `${r.to} — ${statusWord[r.status] ?? r.status}`).join(' · ')}
                </div>
                {x.note && <div className="muted">“{x.note}”</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default PacketPage;
